/**
 * Normalizers for Risk Management backend payloads.
 *
 * The DRF serializers in `backend_django/risk_management/serializers.py`
 * expose nested `*_detail` objects (`project_detail`, `template_detail`,
 * `current_pm`, `last_cycle`, `counts`, `enabled_by_detail`, etc.) for
 * convenience reads.  The frontend pages were originally written against a
 * flatter shape (`project_name`, `template_name`, `current_pm_name`,
 * `current_cycle_name`, `risk_count`, ...).
 *
 * Rather than rewrite every page to dig into nested objects, we run every
 * fetched record through these normalizers once on receipt.  Each helper:
 *
 *   - Keeps the raw backend keys intact (so existing pages can still drill
 *     into `.project_detail` if needed).
 *   - Adds the flat aliases the UI components expect (`project_name`,
 *     `current_cycle_status`, etc.).
 *
 * The helpers are intentionally defensive — any incoming field can be
 * missing or null without throwing.
 */

import type {
  Risk,
  RiskHistoryEntry,
  RiskSnapshot,
  RiskWorksheet,
  RiskWorksheetAssignment,
} from "../Types/RiskManagementTypes";

/* eslint-disable @typescript-eslint/no-explicit-any */

const pickName = (detail: any): string | undefined =>
  detail?.name ?? detail?.full_name ?? undefined;

const pickEmail = (detail: any): string | undefined =>
  detail?.email ?? undefined;

export const normalizeAssignment = (raw: any): RiskWorksheetAssignment => {
  if (!raw) return raw;
  return {
    ...raw,
    assigned_pm_name:
      raw.assigned_pm_name ?? pickName(raw.assigned_pm_detail),
    assigned_pm_email:
      raw.assigned_pm_email ?? pickEmail(raw.assigned_pm_detail),
    assigned_by_name:
      raw.assigned_by_name ?? pickName(raw.assigned_by_detail),
    // Backend calls it `note`; the UI form uses `reason`.
    reason: raw.reason ?? raw.note ?? "",
  };
};

export const normalizeSnapshot = (raw: any): RiskSnapshot => {
  if (!raw) return raw;
  return {
    ...raw,
    cycle_name: raw.cycle_name ?? raw.cycle_detail?.name,
    // Coerce decimals coming through as strings.
    impact: Number(raw.impact ?? 0),
    probability: Number(raw.probability ?? 0),
    exposure: Number(raw.exposure ?? 0),
  };
};

export const normalizeRisk = (raw: any): Risk => {
  if (!raw) return raw;
  const latest = raw.latest_snapshot ?? raw.current_snapshot ?? null;
  return {
    ...raw,
    area_name: raw.area_name ?? raw.area_detail?.name,
    contributor_name:
      raw.contributor_name ?? raw.contributor_detail?.name,
    risk_owner_name:
      raw.risk_owner_name ?? pickName(raw.risk_owner_detail),
    current_snapshot: latest ? normalizeSnapshot(latest) : null,
  };
};

export const normalizeWorksheet = (raw: any): RiskWorksheet => {
  if (!raw) return raw;
  const counts = raw.counts ?? {};
  const lastCycle = raw.last_cycle ?? null;
  const currentPm = raw.current_pm ?? null;
  const history: any[] = Array.isArray(raw.assignment_history)
    ? raw.assignment_history.map(normalizeAssignment)
    : [];
  return {
    ...raw,
    // project_*
    project_name: raw.project_name ?? raw.project_detail?.name,
    project_key: raw.project_key ?? raw.project_detail?.key,
    team_name: raw.team_name ?? raw.project_detail?.team_name,
    // template_*
    template_name: raw.template_name ?? raw.template_detail?.name,
    // current PM — backend returns an object, the UI wants flat keys, but
    // some places also expect `current_pm` to be a plain id string.  Keep
    // the original object too so consumers that already use the nested
    // shape don't break.
    current_pm:
      typeof currentPm === "object" && currentPm !== null
        ? currentPm.id
        : currentPm ?? null,
    current_pm_name:
      raw.current_pm_name ?? pickName(currentPm),
    current_pm_email:
      raw.current_pm_email ?? pickEmail(currentPm),
    // current_cycle_*
    current_cycle_id: raw.current_cycle_id ?? lastCycle?.id ?? null,
    current_cycle_name: raw.current_cycle_name ?? lastCycle?.name ?? null,
    current_cycle_status:
      raw.current_cycle_status ?? lastCycle?.status ?? null,
    // counts
    risk_count: raw.risk_count ?? counts.total_risks ?? 0,
    open_risk_count: raw.open_risk_count ?? counts.open_risks ?? 0,
    high_risk_count: raw.high_risk_count ?? counts.high ?? 0,
    enabled_by_name:
      raw.enabled_by_name ?? pickName(raw.enabled_by_detail),
    assignment_history: history,
  };
};

export const normalizeWorksheetList = (raw: any): RiskWorksheet[] => {
  const list = raw?.results ?? raw ?? [];
  return Array.isArray(list) ? list.map(normalizeWorksheet) : [];
};

export const normalizeRiskList = (raw: any): Risk[] => {
  const list = raw?.results ?? raw ?? [];
  return Array.isArray(list) ? list.map(normalizeRisk) : [];
};

/**
 * Map the `RiskHistorySerializer` payload into the flatter shape the
 * `RiskAuditLog` UI expects.
 *
 * Backend shape (per row):
 *   { history_id, history_date, history_type, history_user: {id,name,email}|null,
 *     object_id, object_type: "Risk"|"RiskSnapshot",
 *     snapshot: { description, impact, probability, ... },
 *     diff: [{ field, old, new }, ...] }
 *
 * Frontend shape (`RiskHistoryEntry`):
 *   { history_id, history_date, history_type, history_user, history_user_name,
 *     model, data, changes: { [field]: [old, new] } }
 */
export const normalizeRiskHistoryEntry = (raw: any): RiskHistoryEntry => {
  const user = raw?.history_user;
  const historyUserId =
    typeof user === "object" && user !== null
      ? user.id ?? null
      : user ?? null;
  const historyUserName =
    raw?.history_user_name ??
    (typeof user === "object" && user !== null
      ? pickName(user) ?? pickEmail(user) ?? null
      : null);

  const changes: Record<string, [unknown, unknown]> = {};
  const diff = Array.isArray(raw?.diff) ? raw.diff : [];
  diff.forEach((d: any) => {
    if (d && typeof d.field === "string") {
      changes[d.field] = [d.old ?? null, d.new ?? null];
    }
  });

  return {
    history_id: raw?.history_id,
    history_date: raw?.history_date,
    history_type: raw?.history_type,
    history_user: historyUserId,
    history_user_name: historyUserName,
    model: (raw?.model ?? raw?.object_type ?? "Risk") as
      | "Risk"
      | "RiskSnapshot",
    object_id: raw?.object_id ?? raw?.id ?? null,
    data: raw?.data ?? raw?.snapshot ?? {},
    changes,
  };
};

export const normalizeRiskHistoryList = (raw: any): RiskHistoryEntry[] => {
  const list = raw?.results ?? raw ?? [];
  return Array.isArray(list) ? list.map(normalizeRiskHistoryEntry) : [];
};

/**
 * Coerce the dashboard heatmap payload into the `{impact, probability, count}`
 * cells format that `RiskHeatmap` consumes.
 *
 * Backend serializers may return one of:
 *   - `heatmap_cells: [{impact, probability, count}, ...]` (preferred)
 *   - `heatmap: [[count, ...], ...]`  (legacy 10x10 grid, [p-1][i-1])
 *   - `heatmap: [{impact, probability, count}, ...]` (already cells)
 */
export const normalizeHeatmapCells = (raw: any): any[] => {
  if (Array.isArray(raw?.heatmap_cells) && raw.heatmap_cells.length) {
    return raw.heatmap_cells;
  }
  const grid = raw?.heatmap;
  if (!Array.isArray(grid)) return [];
  // Already in cells shape if first element is an object with impact/prob.
  if (
    grid.length > 0 &&
    typeof grid[0] === "object" &&
    grid[0] !== null &&
    "impact" in (grid[0] as any) &&
    "probability" in (grid[0] as any)
  ) {
    return grid as any[];
  }
  // 2-D grid → cells.
  const cells: any[] = [];
  grid.forEach((row: any, pIdx: number) => {
    if (!Array.isArray(row)) return;
    row.forEach((count: any, iIdx: number) => {
      const c = Number(count) || 0;
      if (c > 0) {
        cells.push({
          impact: iIdx + 1,
          probability: pIdx + 1,
          count: c,
        });
      }
    });
  });
  return cells;
};

/**
 * Normalize a `ProjectRiskSummary` payload coming back from the dashboard
 * endpoint. Backend exposes both the legacy nested shape
 * (`totals.{high,low,...}`, `area_averages[].area`) and the new flat aliases
 * (`high_count`, `total_risks`, `area_averages[].area_name`, etc.). This
 * helper makes sure the page works against either, and coerces decimal
 * strings into numbers so chart math doesn't blow up.
 */
export const normalizeProjectSummary = (raw: any): any => {
  if (!raw) return raw;
  const totals = raw.totals ?? {};
  const areas = Array.isArray(raw.area_averages) ? raw.area_averages : [];
  const topRisks = Array.isArray(raw.top_risks) ? raw.top_risks : [];
  return {
    ...raw,
    worksheet: raw.worksheet ? normalizeWorksheet(raw.worksheet) : null,
    total_risks: raw.total_risks ?? totals.total_snapshots ?? totals.total_risks ?? 0,
    high_count: raw.high_count ?? totals.high ?? 0,
    open_count: raw.open_count ?? totals.open_risks ?? 0,
    mitigated_count: raw.mitigated_count ?? totals.mitigated ?? 0,
    overdue_count: raw.overdue_count ?? totals.overdue ?? 0,
    area_averages: areas.map((a: any) => ({
      ...a,
      area_name: a.area_name ?? a.area ?? "",
      avg_exposure: Number(a.avg_exposure ?? 0),
      risk_count: a.risk_count ?? a.count ?? 0,
      high_count: a.high_count ?? 0,
      medium_count: a.medium_count ?? 0,
      low_count: a.low_count ?? 0,
    })),
    top_risks: topRisks.map((t: any) => ({
      ...t,
      area_name: t.area_name ?? t.area ?? "",
      contributor_name: t.contributor_name ?? t.contributor ?? "",
      exposure: Number(t.exposure ?? 0),
      impact: Number(t.impact ?? 0),
      probability: Number(t.probability ?? 0),
    })),
    overall_level: raw.overall_level ?? raw.worksheet?.current_overall_level ?? null,
    overall_exposure:
      raw.overall_exposure == null || raw.overall_exposure === ""
        ? null
        : Number(raw.overall_exposure),
    heatmap: normalizeHeatmapCells(raw),
  };
};

/**
 * Normalize an `OrgRiskAggregate` payload. The org dashboard endpoint sends
 * `distribution` as a list of `{level, count}` rows (driven by a DRF
 * Serializer), but the page consumes a `{high, medium, low, ...}` object,
 * and the area / top-risk fields use the same legacy `area`/`contributor`
 * names as the project endpoint.
 */
export const normalizeOrgDashboard = (raw: any): any => {
  if (!raw) return raw;
  const totals = raw.totals ?? {};
  const distMap: Record<string, number> = { HIGH: 0, MEDIUM: 0, LOW: 0 };
  if (Array.isArray(raw.distribution)) {
    raw.distribution.forEach((d: any) => {
      const lvl = String(d?.level ?? "").toUpperCase();
      if (lvl in distMap) distMap[lvl] = Number(d?.count ?? 0);
    });
  } else if (raw.distribution && typeof raw.distribution === "object") {
    // Already the {high, medium, low, ...} shape — keep as-is.
    Object.assign(distMap, {
      HIGH: Number((raw.distribution as any).high ?? distMap.HIGH),
      MEDIUM: Number((raw.distribution as any).medium ?? distMap.MEDIUM),
      LOW: Number((raw.distribution as any).low ?? distMap.LOW),
    });
  }
  const areas = Array.isArray(raw.area_averages) ? raw.area_averages : [];
  const topRisks = Array.isArray(raw.top_risks) ? raw.top_risks : [];
  return {
    ...raw,
    distribution: {
      high: distMap.HIGH,
      medium: distMap.MEDIUM,
      low: distMap.LOW,
      open: Number(totals.open_risks ?? 0),
      mitigated: Number(totals.mitigated ?? 0),
      closed: Number(
        (totals.total_snapshots ?? 0) - (totals.open_risks ?? 0)
      ),
    },
    area_averages: areas.map((a: any) => ({
      ...a,
      area_name: a.area_name ?? a.area ?? "",
      avg_exposure: Number(a.avg_exposure ?? 0),
      risk_count: a.risk_count ?? a.count ?? 0,
      high_count: a.high_count ?? 0,
      medium_count: a.medium_count ?? 0,
      low_count: a.low_count ?? 0,
    })),
    top_risks: topRisks.map((t: any) => ({
      ...t,
      area_name: t.area_name ?? t.area ?? "",
      contributor_name: t.contributor_name ?? t.contributor ?? "",
      exposure: Number(t.exposure ?? 0),
      impact: Number(t.impact ?? 0),
      probability: Number(t.probability ?? 0),
    })),
    heatmap: normalizeHeatmapCells(raw),
  };
};
